import { useEffect, useState } from 'react';
import { useT } from '../../i18n';

/**
 * ホーム画面追加の案内ダイアログ (Phase H、old2 `apps/user/src/components/InstallPrompt.tsx` 逐語移植)。
 *
 * docs/28_offline_plan.md Phase 2 / old2 同等仕様:
 *   - iOS: 共有メニューからの手順を画像なしで案内（beforeinstallprompt は使えない）→ **自動表示はしない**。
 *     ホーム画面追加についての案内は、ハンバーガーメニュー最上部の注意書き
 *     (drawer.ios_standalone_warning) で行う。
 *   - Android (Chrome): beforeinstallprompt を捕捉してワンタップでインストール → overlay modal を自動表示。
 *
 * 表示条件 (old2 踏襲):
 *   - `enabled` が true（既定。地区設定で off の場合 false を渡す）
 *   - 既に PWA 起動（standalone 表示モード）でない
 *   - 過去に「今後表示しない」を選んでいない (DISMISS_KEY)
 *   - Android で beforeinstallprompt が発火した
 *
 * 旧 v2 (user 第 14 弾) との差分:
 *   - inline Card hint → old2 の overlay modal に置換
 *   - iOS Safari の自動 hint 表示を撤去 (old2 通り、iOS は自動表示しない)
 *   - 文言は shared-i18n `install_prompt.*` キー (old2 user.json#installPrompt 逐語)
 *   - DISMISS_KEY を old2 と同じ `'install-prompt-dismissed'` (値 '1') に統一
 */

const DISMISS_KEY = 'install-prompt-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari 専用
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

type OsKind = 'ios-safari' | 'ios-chrome' | 'ios-other' | 'android' | 'other';

function detectOs(): OsKind {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  if (isIOS) {
    // iOS Chrome は CriOS、Firefox は FxiOS、Edge は EdgiOS など
    if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return 'ios-chrome';
    if (/Safari/.test(ua)) return 'ios-safari';
    return 'ios-other';
  }
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/**
 * dismiss 永続化 key (old2 と同一: `install-prompt-dismissed` = '1')。
 * 旧 v2 の `cw.installPrompt.dismissed.v1` から old2 統一のため変更。
 */
export const INSTALL_PROMPT_DISMISS_KEY = DISMISS_KEY;

export interface InstallPromptProps {
  /** 既定 `true`。地区設定で off にする場合 false を渡す (district.installPromptEnabled、本 sprint では未連携)。 */
  enabled?: boolean;
}

export function InstallPrompt({ enabled = true }: InstallPromptProps = {}) {
  const t = useT();
  const [visible, setVisible] = useState(false);
  // OS 判定は mount 時に確定する (deterministic、effect 内 setState を避けるため lazy init)。
  const [os] = useState<OsKind>(() => detectOs());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (isStandalone()) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (dismissed) return;

    if (os === 'android') {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setVisible(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }

    // iOS では InstallPrompt の自動表示は行わない。
    // ホーム画面追加についての案内は、ハンバーガーメニュー最上部の注意書きで行う。
    return undefined;
  }, [enabled, os]);

  if (!visible) return null;

  function close() {
    setVisible(false);
  }

  function dismissForever() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  }

  async function triggerAndroidInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-6 max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-bold mb-3">📲 {t('install_prompt.title')}</h2>
        <p className="mb-3 text-slate-700">{t('install_prompt.android_benefit')}</p>
        {os === 'android' && deferredPrompt && (
          <button
            type="button"
            onClick={() => void triggerAndroidInstall()}
            data-testid="install-prompt-button"
            className="w-full bg-primary-700 hover:bg-primary-800 text-white py-3 rounded-md font-medium mb-2"
          >
            {t('install_prompt.android_install')}
          </button>
        )}
        <button
          type="button"
          onClick={close}
          data-testid="install-prompt-later"
          className="w-full text-slate-700 py-3 mb-1"
        >
          {t('install_prompt.later')}
        </button>
        <button
          type="button"
          onClick={dismissForever}
          data-testid="install-prompt-dismiss"
          className="w-full text-slate-500 hover:text-slate-700 py-2 text-sm underline"
        >
          {t('install_prompt.dont_show_again')}
        </button>
      </div>
    </div>
  );
}
